import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useGame } from '../context/GameContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { safeAlert } from '../utils/gameMonetize';

const { FiArrowLeft, FiMaximize, FiMinimize, FiInfo, FiCoins } = FiIcons;

const ExternalGameViewer = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { game } = location.state || {};
  const [isFullscreen, setIsFullscreen] = useState(false);
  const { addPoints } = useGame();
  const [showRewardToast, setShowRewardToast] = useState(false);

  useEffect(() => {
    if (!game) {
      navigate('/more-games');
      return;
    }
    
    // Initial Start Bonus (Small reward for trying a game)
    const startBonusTimer = setTimeout(() => {
        addPoints(5, `Started ${game.title}`);
        setShowRewardToast(true);
        setTimeout(() => setShowRewardToast(false), 3000);
    }, 5000); // 5 seconds after start

    // Play Time Rewards: 10 points every minute
    const timer = setInterval(() => {
       addPoints(10, `Played ${game?.title || 'External Game'}`);
       safeAlert(`+10 Points for playing ${game?.title}!`);
    }, 60000);

    return () => {
        clearInterval(timer);
        clearTimeout(startBonusTimer);
    };
  }, [game, navigate, addPoints]);

  if (!game) return null;

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
        setIsFullscreen(false);
      }
    }
  };

  return (
    <div className="fixed inset-0 bg-black z-50 flex flex-col">
      {/* Header */}
      <div className={`flex justify-between items-center p-3 bg-gray-900 border-b border-gray-800 ${isFullscreen ? 'hidden' : 'block'}`}>
        <button 
          onClick={() => navigate('/more-games')}
          className="flex items-center space-x-2 text-white hover:text-green-400 transition-colors"
        >
          <SafeIcon icon={FiArrowLeft} className="w-5 h-5" />
          <span className="font-bold">Back</span>
        </button>
        <h1 className="text-white font-bold truncate max-w-[200px]">{game.title}</h1>
        <button 
          onClick={toggleFullscreen}
          className="bg-white/10 p-2 rounded-lg text-white hover:bg-white/20"
        >
          <SafeIcon icon={isFullscreen ? FiMinimize : FiMaximize} className="w-5 h-5" />
        </button>
      </div>

      {/* Iframe Container */}
      <div className="flex-1 relative w-full h-full bg-black">
        <iframe 
          src={game.url} 
          title={game.title}
          className="w-full h-full border-0"
          allow="autoplay; fullscreen; gamepad; accelerometer; gyroscope"
          sandbox="allow-forms allow-modals allow-orientation-lock allow-pointer-lock allow-popups allow-presentation allow-scripts allow-same-origin"
        />
      </div>
      
      {/* Reward Notification Overlay */}
      <AnimatePresence>
        {showRewardToast && (
            <motion.div 
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                className="absolute bottom-20 left-1/2 -translate-x-1/2 bg-green-600/90 backdrop-blur-md px-6 py-3 rounded-full text-white text-sm font-bold flex items-center gap-2 shadow-lg border border-green-400/50 z-50"
            >
                <SafeIcon icon={FiCoins} className="w-5 h-5 text-yellow-300" />
                <span>+5 Points Started Playing!</span>
            </motion.div>
        )}
      </AnimatePresence>

      {/* Persistent Info (Fades out) */}
      <motion.div 
        initial={{ opacity: 1, y: 0 }}
        animate={{ opacity: 0, y: 20 }}
        transition={{ delay: 4, duration: 1 }}
        className="absolute bottom-10 left-1/2 -translate-x-1/2 bg-black/80 px-6 py-3 rounded-full text-white text-sm pointer-events-none border border-white/10"
      >
        Earning: 10 pts/min
      </motion.div>
    </div>
  );
};

export default ExternalGameViewer;